import { pathOr } from 'ramda'
import { PGA_COACH_PERSONAS } from '@lib/pga-coach/constants'

/**
 * Each chip in the Popular Goals rail is a curated shortcut to a combination of
 * existing legacy `/coaches/search` Algolia facet refinements. The chip itself
 * is sticky to the persona id; the underlying refinement values are derived
 * from this map plus the GraphQL refinerData (so experience option strings come
 * from a live source, not a hardcoded list).
 *
 * Shape:
 *   experienceTypeName: top-level Experiences typeName (e.g. "Getting Started").
 *     The preset expands to ALL experience names within that group.
 *   serviceNames:       array of Services serviceName values to refine on.
 *   gender:             single coach `gender` facet value (`F` or `M`).
 *   bookable:           when true, sets `toggle.bookable` = true.
 *
 * `military-veterans` has no clean coach-index facet today, so its preset is
 * empty. The chip still toggles the helper banner.
 */
export const PERSONA_PRESETS = {
  'start-golf': {
    experienceTypeName: 'Getting Started',
    serviceNames: ['Group'],
    bookable: true,
  },
  'play-better': {
    experienceTypeName: 'Having More Fun',
  },
  'break-100': {
    experienceTypeName: 'Improving Your Game',
    serviceNames: ['Individual'],
    bookable: true,
  },
  juniors: {
    serviceNames: ['Youth', 'Teen', 'Group'],
  },
  'womens-golf': {
    gender: 'F',
  },
  'military-veterans': {},
}

export const isValidPersonaId = (personaId) => Boolean(PERSONA_PRESETS[personaId])
  && PGA_COACH_PERSONAS.some((p) => p.id === personaId)

const expandExperienceNames = (typeName, refinerData) => {
  if (!typeName) return []
  const types = pathOr([], ['Experiences', 'experienceTypes'], refinerData)
  const match = types.find((t) => t.typeName === typeName)
  return (match?.experiences || []).map((e) => e.experienceName).filter(Boolean)
}

/**
 * Resolves the concrete Algolia refinement values a persona chip contributes,
 * given the live refiner option lists from GraphQL.
 *
 * Returns an object describing the patch (never null when persona is valid):
 *   refinementList: { [attribute]: string[] }
 *   toggle:         { bookable?: true }
 *
 * The values may be empty arrays when the GraphQL data is missing — callers
 * should treat empty values as no-ops.
 */
export const resolvePersonaRefinementPatch = (personaId, refinerData) => {
  if (!isValidPersonaId(personaId)) return null
  const def = PERSONA_PRESETS[personaId]
  const experiences = expandExperienceNames(def.experienceTypeName, refinerData)
  return {
    refinementList: {
      ...(experiences.length && {
        'experienceTypes.experiences.experienceName': experiences,
      }),
      ...(def.serviceNames?.length && {
        'serviceTypes.services.serviceName': [...def.serviceNames],
      }),
      ...(def.gender && { gender: [def.gender] }),
    },
    toggle: {
      ...(def.bookable && { bookable: true }),
    },
  }
}

// `react-instantsearch-dom` URL serialization sometimes hands back a single
// string for `refinementList[attr]` instead of an array (this happens when
// only one value is active for an attribute). Coerce to an array at the
// boundary so the merge/subtract helpers can stay array-only.
const toArray = (v) => {
  if (Array.isArray(v)) return v
  if (v == null || v === '') return []
  return [v]
}

const uniqMerge = (a, b) => Array.from(new Set([...toArray(a), ...toArray(b)]))

const subtractValues = (current, remove) => {
  const currentArr = toArray(current)
  const removeArr = toArray(remove)
  if (!removeArr.length) return currentArr
  const removeSet = new Set(removeArr)
  return currentArr.filter((v) => !removeSet.has(v))
}

/**
 * Merge a persona refinement patch onto an existing searchState, unioning
 * refinement values rather than replacing them. The persona id is stored as a
 * top-level `persona` key so callers can detect chip selection without having
 * to reverse-engineer the patch.
 */
export const applyPersonaPreset = (searchState, personaId, refinerData) => {
  const state = searchState || {}
  const patch = resolvePersonaRefinementPatch(personaId, refinerData)
  const baseRefinementList = state.refinementList || {}
  const baseToggle = state.toggle || {}
  const patchRefinementList = patch?.refinementList || {}
  const patchToggle = patch?.toggle || {}

  const mergedRefinementList = { ...baseRefinementList }
  Object.keys(patchRefinementList).forEach((attr) => {
    mergedRefinementList[attr] = uniqMerge(baseRefinementList[attr], patchRefinementList[attr])
  })

  const mergedToggle = { ...baseToggle, ...patchToggle }

  return {
    ...state,
    persona: personaId,
    ...(Object.keys(mergedRefinementList).length && { refinementList: mergedRefinementList }),
    ...(Object.keys(mergedToggle).length && { toggle: mergedToggle }),
  }
}

/**
 * Remove the values a persona patch contributes from a searchState. Used when
 * the user deselects a chip or switches to a different chip. Values that the
 * user added independently of the preset are preserved.
 */
export const stripPersonaPreset = (searchState, personaId, refinerData) => {
  const state = searchState || {}
  const patch = resolvePersonaRefinementPatch(personaId, refinerData)
  const baseRefinementList = state.refinementList || {}
  const baseToggle = state.toggle || {}

  const nextRefinementList = { ...baseRefinementList }
  const patchRefinementList = patch?.refinementList || {}
  Object.keys(patchRefinementList).forEach((attr) => {
    const next = subtractValues(baseRefinementList[attr], patchRefinementList[attr])
    if (next.length) nextRefinementList[attr] = next
    else delete nextRefinementList[attr]
  })

  const nextToggle = { ...baseToggle }
  if (patch?.toggle?.bookable) delete nextToggle.bookable

  const next = { ...state }
  delete next.persona

  if (Object.keys(nextRefinementList).length) next.refinementList = nextRefinementList
  else delete next.refinementList

  if (Object.keys(nextToggle).length) next.toggle = nextToggle
  else delete next.toggle

  return next
}

/**
 * Atomically swap personas: strip the previous chip's preset before applying
 * the next one. `nextPersonaId` may be `null`/`''` to deselect entirely.
 */
export const swapPersonaPreset = (searchState, nextPersonaId, refinerData) => {
  const state = searchState || {}
  const previousPersonaId = state.persona
  const stripped = previousPersonaId
    ? stripPersonaPreset(state, previousPersonaId, refinerData)
    : state
  if (!nextPersonaId) return stripped
  return applyPersonaPreset(stripped, nextPersonaId, refinerData)
}
